import { useState, useEffect, useContext } from "react";
import ReactDatePicker from "react-datepicker";
import {
  Button,
  Dropdown,
  DropdownItemProps,
  Form,
  FormProps,
  Input,
  Modal,
  Segment,
  Table,
  TextArea,
} from "semantic-ui-react";
import { dialogControl } from "../controls/dialog-control";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { hostUrl, restRequest } from "../http";
import { PageData } from "../models/common_model";
import { DropdownOption } from "../models/testing_model";
import {
  AnalyzedUnit,
  AnalyzedUnitConfig,
} from "../models/analyzed_unit_model";
import { decodeHTMLEntities, fieldError, formatDateTime } from "../utils";
import { JournalizationComponent } from "./commons/JournalizationComponent";
import { Page } from "./commons/Page";
import { AnalyzedUnitEditor } from "./AnalyzedUnitEditor";
import _ from "lodash";
import { NavLink, Switch, useHistory } from "react-router-dom";
import { RouteWithSubRoutes } from "../routes";
import { DetailIdContext } from "../context";

export function AnalyzedUnitComponent(props: { routes: any }) {
  const currentUser = useCurrentUser();

  const [detailId, setDetailId] = useState(0);

  const history = useHistory();

  const [searchData, setSearchField] = useFormData<any>({
    product: "",
    station: "",
    order: "",
    measurement_status: [1, 2, 3],
    updated_status: [0, 1, 2],
    case_status: [0, 1],
    project_id: -1,
    testing_id: -1,
  });
  const [waiveUnits, setSearchData, searchLoading] = useFormConnect<
    any,
    PageData<AnalyzedUnit>
  >({}, "analyzed_unit/search");

  const [config, setConfig] = useState<AnalyzedUnitConfig>();
  const [productionOptions, setProductionOptions] = useState<
    DropdownItemProps[]
  >([]);
  const [productionAdditionOptions, setProductionAdditionOptions] = useState<
    DropdownItemProps[]
  >([]);

  const editModalControl = useModalControl<boolean>(false);
  const [currentEditId, setCurrentEditId] = useState<number>();
  const journalizationModalControl = useModalControl();
  const [currentJournalizationId, setCurrentJournalizationId] =
    useState<number>();
  const [reloadNumber, setReloadNumber] = useState(0);
  const [rootCausesOptions, setRootCauseOptions] = useState<
    DropdownItemProps[]
  >([]);

  useEffect(() => {
    restRequest<DropdownItemProps[]>(
      `commons/root_causes/search_for_selection`
    ).then((options) => {
      setRootCauseOptions(options);
    });
  }, [reloadNumber]);

  useEffect(() => {
    restRequest<AnalyzedUnitConfig>("analyzed_unit/config").then(setConfig);
  }, []);

  useEffect(() => {
    setProductionOptions([
      { text: "All", value: "", key: "" },
      ...(config?.products || [])
        .sort()
        .map((p) => ({ text: p, value: p, key: p })),
      ...productionAdditionOptions,
    ]);
  }, [config?.products, productionAdditionOptions]);

  useEffect(() => {
    if (detailId) {
      setCurrentEditId(detailId);
      editModalControl.data = !currentUser.permissions.analyzed_unit.update;
      editModalControl.setOpen(true);
    }
  }, [detailId]);

  return (
    <>
      <Segment.Group>
        <Segment attached="top">
          <Form
            onSubmit={(e) => setSearchData(searchData)}
            size="mini"
            className="list-search"
            loading={searchLoading}
          >
            <Form.Group>
              <Form.Input inline label="Product:" style={{ minWidth: "10rem" }}>
                <Dropdown
                  name="product"
                  value={searchData.product || ""}
                  onChange={setSearchField}
                  options={productionOptions}
                  search
                  selection
                  allowAdditions
                  onAddItem={(e, d) => {
                    setProductionAdditionOptions((os) => [
                      ...os,
                      {
                        key: d.value,
                        text: d.value as string,
                        value: d.value as string,
                        isAdded: true,
                      },
                    ]);
                  }}
                  fluid
                ></Dropdown>
              </Form.Input>

              <Form.Input inline label="Serial:">
                <Input
                  name="serial"
                  value={searchData["serial"] || ""}
                  onChange={setSearchField}
                />
              </Form.Input>

              <Form.Group inline style={{ paddingRight: "1rem" }}>
                <label>Measurement Date:&nbsp;</label>
                <Form.Field inline style={{ paddingRight: 0 }}>
                  <ReactDatePicker
                    showTimeSelect={false}
                    selected={
                      searchData["date_start"]
                        ? new Date(searchData["date_start"])
                        : null
                    }
                    dateFormat="yyyy-MM-d"
                    onChange={(d: Date) => {
                      setSearchField(null, {
                        name: "date_start",
                        value: d && d.toISOString(),
                      });
                    }}
                  ></ReactDatePicker>
                </Form.Field>
                <div>&nbsp;-&nbsp;</div>
                <Form.Field inline style={{ paddingRight: 0 }}>
                  <ReactDatePicker
                    showTimeSelect={false}
                    selected={
                      searchData["date_end"]
                        ? new Date(searchData["date_end"])
                        : null
                    }
                    dateFormat="yyyy-MM-d"
                    onChange={(d: Date) => {
                      setSearchField(null, {
                        name: "date_end",
                        value: d && d.toISOString(),
                      });
                    }}
                  ></ReactDatePicker>
                </Form.Field>
              </Form.Group>
              <Form.Group inline style={{ paddingRight: "1rem" }}>
                <label style={{ whiteSpace: "nowrap" }}>
                  Case Status:&nbsp;
                </label>
                <Dropdown
                  multiple
                  name="case_status"
                  value={searchData.case_status || ""}
                  onChange={setSearchField}
                  options={(config?.AVAILABLE_CASE_STATUSES || []).map(
                    (v, i) => ({ key: i + 1, text: v, value: i })
                  )}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Group>
            </Form.Group>
            <Form.Group>
              <Form.Group inline style={{ paddingRight: "1rem" }}>
                <label style={{ whiteSpace: "nowrap" }}>
                  Sequence Status:&nbsp;
                </label>
                <Dropdown
                  multiple
                  name="measurement_status"
                  value={searchData.measurement_status || ""}
                  onChange={setSearchField}
                  options={["Pass", "Fail", "Invalid", "Stopped"].map(
                    (v, i) => ({ key: i + 1, text: v, value: i + 1 })
                  )}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Group>

              <Form.Group inline style={{ paddingRight: "1rem" }}>
                <label style={{ whiteSpace: "nowrap" }}>
                  Updated Status:&nbsp;
                </label>
                <Dropdown
                  multiple
                  placeholder="Updated Status"
                  name="updated_status"
                  value={searchData.updated_status || ""}
                  onChange={setSearchField}
                  options={
                    config?.AVAILABLE_UPDATE_STATUSES.map((v, i) => ({
                      key: i,
                      text: i == 0 ? "not set" : v,
                      value: i,
                    })) || []
                  }
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Group>

              <Form.Group
                inline
                style={{ paddingRight: "1rem", minWidth: "1rem" }}
              >
                <label style={{ whiteSpace: "nowrap" }}>
                  Root Cause:&nbsp;
                </label>
                <Dropdown
                  name="root_cause"
                  value={searchData.root_cause || ""}
                  onChange={setSearchField}
                  options={[
                    { text: "All", value: " ", key: " " } as DropdownItemProps,
                  ].concat(rootCausesOptions)}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Group>

              <Form.Input inline label="Action:" style={{ minWidth: "10rem" }}>
                <Input
                  name="action"
                  value={searchData["action"] || ""}
                  onChange={setSearchField}
                />
              </Form.Input>

              <div>
                <Button icon="search" type="submit"></Button>
                <Button
                  as="a"
                  icon="file excel outline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(
                      hostUrl(
                        "analyzed_unit/export?search=" +
                          encodeURI(JSON.stringify(searchData))
                      ),
                      "_blank"
                    );
                  }}
                ></Button>
                <Button
                  as="a"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(
                      hostUrl(
                        "analyzed_unit/export_sequences?search=" +
                          encodeURI(JSON.stringify(searchData))
                      ),
                      "_blank"
                    );
                  }}
                >
                  Export Sequences
                </Button>
              </div>
            </Form.Group>
          </Form>
        </Segment>
        <Segment attached="bottom">
          <Table striped>
            <Table.Header>
              <Table.Row>
                <th>Product</th>
                <th>Order</th>
                <th>Serial</th>
                <th>Issue Name</th>
                <th>Root Cause</th>
                <th>Resolved By</th>
                <th>Created At</th>
                <th>Owner</th>
                <th>Case Status</th>
                <th>
                  {currentUser.permissions.analyzed_unit.create && (
                    <Button
                      color="blue"
                      icon="add"
                      content="Analyzed Unit"
                      size="mini"
                      onClick={(e) => {
                        setCurrentEditId(undefined);
                        editModalControl.data = false;
                        editModalControl.setOpen(true);
                      }}
                    />
                  )}
                </th>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {waiveUnits?.items.map((unit) => {
                return (
                  <tr key={unit.id}>
                    <td>{unit.product || ""}</td>
                    <td>{unit.order || ""}</td>
                    <td>{unit.serial}</td>
                    <td>{unit?.issue_overview?.name}</td>
                    <td>{unit.root_cause}</td>
                    <td>
                      {config && unit.resolved_by || ""}
                    </td>
                    <td>{formatDateTime(unit.created_at)}</td>
                    <td>{unit.owner}</td>
                    <td>
                      {(config &&
                        config.AVAILABLE_CASE_STATUSES[unit.case_status]) ||
                        ""}
                    </td>
                    <td>
                      <Button
                        as={NavLink}
                        to={`/analyzed_unit/${unit.id}`}
                        color="blue"
                        icon={
                          currentUser.permissions.analyzed_unit.update
                            ? "edit"
                            : "search"
                        }
                        size="mini"
                      ></Button>

                      {currentUser.permissions.analyzed_unit.delete && (
                        <Button
                          onClick={(e) => {
                            dialogControl.deletionConfirm(
                              unit.serial,
                              (argee) => {
                                if (argee) {
                                  return restRequest(
                                    `analyzed_unit/${unit.id}`,
                                    null,
                                    "delete"
                                  ).then(() => {
                                    setSearchData((searchData: any) => ({
                                      ...searchData,
                                      refresh: (searchData.refresh || 1) + 1,
                                    }));
                                  });
                                }
                                return Promise.resolve();
                              }
                            );
                          }}
                          color="red"
                          icon="delete"
                          size="mini"
                        />
                      )}

                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentJournalizationId(unit.id);
                          journalizationModalControl.setOpen(true);
                        }}
                        icon="history"
                        size="mini"
                      ></Button>
                    </td>
                  </tr>
                );
              })}
            </Table.Body>
          </Table>
          <Page data={waiveUnits} updatePage={setSearchData}></Page>
        </Segment>
      </Segment.Group>

      <DetailIdContext.Provider value={[detailId, setDetailId]}>
        <Switch>
          {props.routes.map((route: any, i: any) => (
            <RouteWithSubRoutes key={i} {...route} />
          ))}
        </Switch>
      </DetailIdContext.Provider>

      <AnalyzedUnitEditor
        id={currentEditId}
        modalControl={editModalControl}
        onFinish={(result) => {
          setSearchData((searchData: any) => ({
            ...searchData,
            refresh: (searchData.refresh || 1) + 1,
          }));
          setReloadNumber((n) => n + 1);
        }}
        onExit={() => {
          editModalControl.setOpen(false);
          setDetailId(0);
          history.push("/analyzed_unit");
        }}
      ></AnalyzedUnitEditor>

      {config && currentJournalizationId && (
        <Journalization
          id={currentJournalizationId}
          modelControl={journalizationModalControl}
          config={config}
        />
      )}
    </>
  );
}

function Journalization(props: {
  id: number;
  modelControl: ModalControlData;
  config: AnalyzedUnitConfig;
}) {
  const { config } = props;
  return (
    <Modal {...props.modelControl.props} size="fullscreen">
      <Modal.Header>Journalization</Modal.Header>
      <Modal.Content>
        <JournalizationComponent
          containerId={props.id}
          containerType={1}
          detailFields={[
            { key: "product", name: "Product" },
            { key: "serial", name: "Serial" },
            { key: "order", name: "Order" },
            { key: "issue_overview_id", name: "Issue Name",
              formater: (v) => v ? restRequest('analyzed_unit/get_issue_name/'+v, null, 'get').then(v => v) : ''
            },
            {
              key: "failure_description",
              name: "Failure Description",
              formater: (v) =>
                v ? <div dangerouslySetInnerHTML={{ __html: v }}></div> : v,
            },
            {
              key: "failure_analysis",
              name: "Failure Analysis",
              formater: (v) =>
                v ? <div dangerouslySetInnerHTML={{ __html: v }}></div> : v,
            },
            {
              key: "action",
              name: "Action",
              formater: (v) =>
                v ? <div dangerouslySetInnerHTML={{ __html: v }}></div> : v,
            },
            {
              key: "remark",
              name: "Remark",
              formater: (v) =>
                v ? <div dangerouslySetInnerHTML={{ __html: v }}></div> : v,
            },
            { key: "root_cause", name: "Root Cause" },
            { key: "case_status", name: "Case Status" },
            { key: "resolved_by", name: "Resolved By" },
            { key: "owner", name: "Owner" },
            {
              key: "pictures",
              name: "Pictures",
              isList: true,
              formater: (v) => v["name"],
            },
            {
              key: "summaries",
              name: "Summaries",
              isList: true,
              formater: (v) => v["file_path"],
              children: [
                {
                  key: "update_status",
                  name: "Update Status",
                  formater: (v) => config.AVAILABLE_UPDATE_STATUSES[v],
                },
                { key: "remark", name: "Remark" },
              ],
            },
          ]}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          as="a"
          onClick={(e) => {
            e.preventDefault();

            props.modelControl.setOpen(false);
          }}
        >
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
